import {
  BNBox,
  BNButton,
  BNCard,
  BNCardContent,
  BNRichText,
  BNSlider,
  Culture,
  RegionId,
  currencyFormatter,
  numberFormatter,
} from "@bn-monorepo/js-packages";
import { Grid } from "@mui/material";
import { Paper, useTheme, Stack } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { IMinimalSlider } from "../../Slider/MinimalSlider";
import RichText from "../../../../../CmsComponents/RichText/RichText";
import { SettingsContext } from "../../../../../../settingsContext";
import { StringContext } from "../../../../../../StringContext";
import { calculateLoan } from "@bn-monorepo/js-packages/src/Helpers/calculators/calculateLoan";
import BNAlert from "@bn-monorepo/js-packages/src/Components/BNAlert/BNAlert";
import { Image } from "../../Section/Section";

export interface RepaymentPlanYear {
  year: number;
  total: number;
  outstanding: number;
  months: RepaymentPlanMonth[];
}
export interface RepaymentPlanMonth {
  monthNum: number;
  interestPct: number;
  interestAmount: number;
  repaymentAmount: number;
  totalAmount: number;
  installmentFee: number;
}
export interface CalculateLoanResult {
  years: RepaymentPlanYear[];
  amount: number;
  interestSum: number;
  capitalSum: number;
  sum: number;
  effectiveInterest: number;
}

interface ILoanCalculatorProps {
  image?: Image;
  headingContent: string;
  loanAmountSlider: IMinimalSlider;
  installmentFee: number;
  setupFee: number;
  setupFeeIntervals: SetupFeeInterval[];
  repaymentPeriodSlider: IMinimalSlider;
  nominalInterestRateSlider: IMinimalSlider;
  loanType: "annuity" | "serial";
  priceExample?: string;
  useSlimLayout?: boolean;
  ctaButton: any;
  threshold?: number;
  thresholdWarning?: string;
}

export interface SetupFeeInterval {
  loanAmountFrom: number;
  loanAmountTo: number;
  setupFeeAmount: number;
}

export enum LoanType {
  Annuity = 1,
  Serial = 2,
}

export interface LoanParams {
  loanType: LoanType;
  loanAmount: number;
  numberOfMonths: number;
  interestRate: number;
  setupFee: number;
  installmentFee: number;
  addFeeToLoanAmount: boolean;
}

const resolveLoanType = (param: string | null) => {
  switch (param) {
    case "serial":
      return LoanType.Serial;
    default:
      return LoanType.Annuity;
  }
};

const calculateSetupFee = (intervals: SetupFeeInterval[], loanAmount: number): number => {
  const foundInterval = intervals?.find((i) => loanAmount >= i.loanAmountFrom && loanAmount <= i.loanAmountTo);
  return foundInterval ? foundInterval.setupFeeAmount : 0;
};

const LoanCalculatorSlim: React.FC<ILoanCalculatorProps> = (props) => {
  const theme = useTheme();
  const strings = useContext(StringContext);
  const settings = useContext(SettingsContext);

  const calculatorParams = {
    image: props.image,
    headingContent: props.headingContent,
    loanAmountSlider: props.loanAmountSlider,
    repaymentPeriodSlider: props.repaymentPeriodSlider,
    nominalInterestRateSlider: props.nominalInterestRateSlider,
    priceExample: props.priceExample,
  };

  const resolveProps = (props: ILoanCalculatorProps) => {
    const image = props.image;
    const headingContent = props.headingContent;
    const loanAmountSlider = props.loanAmountSlider;
    const repaymentPeriodSlider = props.repaymentPeriodSlider;
    const priceExample = props.priceExample;
    const nominalInterestRateSlider = props.nominalInterestRateSlider;

    const loanAmount = loanAmountSlider?.default ?? 0;
    const installmentFee = props.installmentFee ?? 0;
    const setupFee = props.setupFee > 0 ? props.setupFee : calculateSetupFee(props.setupFeeIntervals, loanAmount);
    const numberOfMonths = repaymentPeriodSlider?.default ? repaymentPeriodSlider.default * 12 : 1;
    const interestRate = nominalInterestRateSlider?.default ?? 0;
    const loanType = resolveLoanType(props.loanType);
    const ctaButton = props.ctaButton;

    return {
      image,
      headingContent,
      loanAmountSlider,
      loanAmount,
      installmentFee,
      setupFee,
      repaymentPeriodSlider,
      nominalInterestRateSlider,
      numberOfMonths,
      interestRate,
      loanType,
      priceExample,
      ctaButton,
    };
  };

  const { loanAmount, installmentFee, setupFee, numberOfMonths, interestRate, loanType, ctaButton } =
    resolveProps(props);

  const [loanParams, setLoanParams] = useState<LoanParams>({
    loanType: loanType,
    loanAmount: loanAmount,
    numberOfMonths: numberOfMonths,
    interestRate: interestRate,
    setupFee: setupFee,
    installmentFee: installmentFee,
    addFeeToLoanAmount: true,
  });

  const [calculateLoanResult, setcalculateLoanResult] = useState<CalculateLoanResult>();

  const [repaymentYears, setRepaymentYears] = useState<number>(numberOfMonths / 12);

  const setLoanAmount = (e) => {
    const fee = calculateSetupFee(props.setupFeeIntervals, e.target.value);
    setLoanParams({ ...loanParams, loanAmount: e.target.value, setupFee: fee });
  };

  const setNumberOfYears = (e) => {
    const numberOfYears = e.target.value;
    setLoanParams({ ...loanParams, numberOfMonths: numberOfYears * 12 });
    setRepaymentYears(numberOfYears);
  };

  useEffect(() => {
    setcalculateLoanResult(calculateLoan(loanParams));
  }, [loanParams]);

  const formatCurrency = (number: number | undefined) => {
    const region = settings.region;
    const cf = currencyFormatter();
    return cf(number ?? 0, region);
  };

  const formatNumber = (number: number | undefined, showDecimals: boolean | undefined = false) => {
    const region = settings.region;
    let locales: Culture | undefined = undefined;
    switch (region) {
      case RegionId.Norway:
        locales = "nb-NO";
        break;
      case RegionId.Sweden:
        locales = "sv-SE";
        break;
      case RegionId.Denmark:
        locales = "da-DK";
        break;
      case RegionId.Finland:
        locales = "fi-FI";
        break;
      case RegionId.Spain:
        locales = "es-ES";
        break;
      case RegionId.Germany:
        locales = "de-DE";
        break;
    }
    const nf = numberFormatter(locales);
    return nf(number ?? 0, showDecimals);
  };
  const sliderMarks = [] as any;
  const repaymentSliderMinValue = calculatorParams.repaymentPeriodSlider.min;
  const repaymentSliderMaxValue = calculatorParams.repaymentPeriodSlider.max;
  const repaymentSliderStepValue = calculatorParams.repaymentPeriodSlider.step;
  for (let i = repaymentSliderMinValue; i <= repaymentSliderMaxValue; i += repaymentSliderStepValue) {
    const mark = { value: i, label: i.toString() };
    sliderMarks.push(mark);
  }
  const queryParameterName = calculatorParams?.loanAmountSlider?.queryParameterName;
  return (
    <Grid container>
      <Grid sx={{ width: "100%", margin: 2 }}>
        <Paper
          sx={{
            maxWidth: "80rem",
            marginLeft: "auto",
            marginRight: "auto",
            padding: { xs: 2, md: 6 },
            borderRadius: { xs: 4, md: 8 },
            marginTop: 3,
          }}
        >
          <Stack spacing={2} sx={{}}>
            <BNBox
              sx={{
                fontSize: { xs: "1.6rem", md: "1.8rem" },
                fontWeight: "700",
                textAlign: "left",
              }}
            >
              <BNRichText html={calculatorParams.headingContent} />
            </BNBox>
            <BNCard sx={{ mb: 0 }}>
              <BNCardContent sx={{ backgroundColor: theme.vars.palette.secondary.lighter }}>
                <BNBox sx={{ display: "flex", justifyContent: "space-between" }}>
                  <BNBox
                    sx={{
                      fontWeight: "700",
                      fontSize: { xs: "1.3rem", md: "1.5rem" },
                    }}
                  >
                    {strings.loanCalculator_Loan}
                  </BNBox>
                  <BNBox>{formatCurrency(loanParams.loanAmount)}</BNBox>
                </BNBox>
                <BNBox sx={{}}>
                  <BNSlider
                    title={strings.loanCalculator_Loan}
                    min={calculatorParams.loanAmountSlider.min}
                    max={calculatorParams.loanAmountSlider.max}
                    step={calculatorParams.loanAmountSlider.step}
                    onChange={setLoanAmount}
                    color="primary"
                    minLabel={formatCurrency(calculatorParams.loanAmountSlider.min)}
                    maxLabel={formatCurrency(calculatorParams.loanAmountSlider.max)}
                    whiteTrack
                  />
                </BNBox>
                <BNBox>
                  {props.loanAmountSlider.threshold != 0 &&
                    props.loanAmountSlider.threshold &&
                    loanParams.loanAmount > props.loanAmountSlider.threshold &&
                    props.loanAmountSlider.thresholdWarning && (
                      <BNAlert severity="warning">{props.loanAmountSlider.thresholdWarning}</BNAlert>
                    )}
                </BNBox>
                {calculatorParams.repaymentPeriodSlider.min !== calculatorParams.repaymentPeriodSlider.max && (
                  <BNBox sx={{ paddingTop: 1, marginBottom: -2 }}>
                    <BNBox
                      sx={{
                        fontWeight: "700",
                        paddingBottom: "0.8rem",
                        fontSize: { xs: "1.3rem", md: "1.5rem" },
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <BNBox>{strings.loanCalculator_RepaymentPeriod}</BNBox>
                      <BNBox>
                        {repaymentYears} {props.repaymentPeriodSlider.unitSymbol}
                      </BNBox>
                    </BNBox>
                    <BNBox sx={{}}>
                      <BNSlider
                        min={calculatorParams.repaymentPeriodSlider.min}
                        max={calculatorParams.repaymentPeriodSlider.max}
                        step={1}
                        onChange={setNumberOfYears}
                        color="primary"
                        minLabel={
                          calculatorParams.repaymentPeriodSlider.min + " " + props.repaymentPeriodSlider.unitSymbol
                        }
                        maxLabel={
                          calculatorParams.repaymentPeriodSlider.max + " " + props.repaymentPeriodSlider.unitSymbol
                        }
                        whiteTrack
                        value={repaymentYears}
                      />
                    </BNBox>
                  </BNBox>
                )}
                {ctaButton && (
                  <BNBox
                    sx={{
                      textAlign: ctaButton.textAlign,
                      display: "flex",
                      justifyContent: "center",
                      position: "relative",

                      [theme.breakpoints.up("xs")]: {
                        top: "-4.8rem",
                      },

                      [theme.breakpoints.up("lg")]: {
                        top: "-5.6rem",
                      },

                      mb: 0,
                    }}
                  ></BNBox>
                )}
              </BNCardContent>
            </BNCard>
            <BNCard id="loan-calculator-output">
              <BNCardContent sx={{ backgroundColor: theme.vars.palette.background.paper }}>
                <BNBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    fontSize: { xs: "1.3rem", md: "1.5rem" },
                  }}
                >
                  <BNBox
                    sx={{ display: "flex", fontWeight: 700, paddingBottom: 1 }}
                  >{`${strings.loanCalculator_Invoice}`}</BNBox>
                  <BNBox sx={{ display: "flex", fontWeight: 500 }}>
                    {`${formatCurrency(calculateLoanResult?.years[0]?.months[0]?.totalAmount)}`}
                  </BNBox>
                </BNBox>
                <BNBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignContent: "space-between",
                    justifyContent: "space-between",
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                    paddingBottom: 1,
                  }}
                >
                  <BNBox
                    sx={{ display: "flex", fontWeight: 700 }}
                  >{`${strings.loanCalculator_YearlyNominalInterest}`}</BNBox>
                  <BNBox sx={{ display: "flex", fontWeight: 500 }}>
                    {`${formatNumber(calculatorParams.nominalInterestRateSlider.default, true)} %`}
                  </BNBox>
                </BNBox>
                <BNBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignContent: "space-between",
                    justifyContent: "space-between",
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                    paddingBottom: 1,
                  }}
                >
                  <BNBox sx={{ display: "flex", fontWeight: 700 }}>{`${strings.loanCalculator_EffInterest}`}</BNBox>
                  <BNBox sx={{ display: "flex", fontWeight: 500 }}>
                    {`${formatNumber(calculateLoanResult?.effectiveInterest, true)} %`}
                  </BNBox>
                </BNBox>
                <BNBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignContent: "space-between",
                    justifyContent: "space-between",
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                    paddingBottom: 1,
                  }}
                >
                  <BNBox sx={{ display: "flex", fontWeight: 700 }}>{strings.loanCalculator_LoanCost}</BNBox>
                  <BNBox sx={{ display: "flex", fontWeight: 500 }}>
                    {formatCurrency((calculateLoanResult?.sum ?? 0) - (calculateLoanResult?.amount ?? 0))}
                  </BNBox>
                </BNBox>
                <BNBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignContent: "space-between",
                    fontSize: { xs: "1.1rem", md: "1.3rem" },
                    justifyContent: "space-between",
                  }}
                >
                  <BNBox sx={{ display: "flex", fontWeight: 700 }}>{strings.loanCalculator_LoanTotalCost}</BNBox>
                  <BNBox sx={{ display: "flex", fontWeight: 500 }}>{formatCurrency(calculateLoanResult?.sum)}</BNBox>
                </BNBox>
              </BNCardContent>
            </BNCard>
            <BNBox>
              <BNButton
                sx={{
                  minWidth: "100%",
                  fontWeight: "bold",
                  height: "5rem",
                  m: 0,
                  fontSize: { xs: "1.3rem", md: "1.5rem" },
                }}
                color="primary"
                href={
                  queryParameterName
                    ? `${ctaButton.href}?${queryParameterName}=${loanParams.loanAmount}`
                    : ctaButton.href
                }
              >
                {ctaButton.text}
              </BNButton>
            </BNBox>
            <RichText html={calculatorParams.priceExample ?? strings.loanCalculator_ExampleText} />
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default LoanCalculatorSlim;
